import { Database, ShieldCheck, Wand2, FileJson } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import TestDataGenerator from "@/components/TestDataGenerator";
import ToolPageShell from "@/components/tools/ToolPageShell";
import { ClientOnly } from "@tanstack/react-router";

const faqs = [
  { q: "How realistic is the data?", a: "It uses Faker.js under the hood — names, emails, addresses, companies and more all look real but are completely fake." },
  { q: "Can I export it?", a: "Yes — copy or download as JSON, CSV or SQL INSERT statements with your chosen table name." },
  { q: "Is the data deterministic?", a: "Set a numeric seed to get the exact same dataset every time — useful for tests and reproducible demos." },
  { q: "What's the row limit?", a: "1,000 rows per generation. Need more? Run it multiple times with different seeds and concatenate." },
];

const TestDataGeneratorPage = () => (
  <>
    <SEOHead
      title="Free Test Data Generator — Realistic JSON, CSV & SQL"
      description="Generate realistic fake datasets for testing and demos. Custom schema, JSON / CSV / SQL export, optional seed for reproducible runs. Free."
      path="/tools/test-data-generator"
      keywords="test data generator, fake data generator, mock data, faker.js online, JSON test data, CSV test data, SQL insert generator, free test data"
      breadcrumbs={[
        { name: "Home", path: "/" },
        { name: "Free Tools", path: "/tools" },
        { name: "Test Data Generator", path: "/tools/test-data-generator" },
      ]}
      jsonLd={[
        { "@context": "https://schema.org", "@type": "WebApplication", name: "NETIUS Test Data Generator", url: "https://netius.com/tools/test-data-generator", applicationCategory: "DeveloperApplication", operatingSystem: "Any (Web)", offers: { "@type": "Offer", price: "0", priceCurrency: "USD" } },
        { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
      ]}
    />
    <ToolPageShell
      title="Test Data Generator"
      subtitle="Build a custom schema and generate realistic fake data — names, emails, addresses, dates, prices and more. Export as JSON, CSV or SQL."
      badges={[
        { icon: ShieldCheck, label: "Runs in your browser" },
        { icon: Database, label: "JSON · CSV · SQL" },
        { icon: Wand2, label: "Reproducible with seeds" },
      ]}
      featuresHeading="What it does"
      features={[
        { icon: Wand2, title: "Custom schema", description: "Add fields, pick from 25+ realistic types — people, places, products, dates and more." },
        { icon: FileJson, title: "Three export formats", description: "Copy or download as JSON, CSV, or SQL INSERT statements with your table name." },
        { icon: Database, title: "Seeded reproducibility", description: "Set a seed to get the exact same dataset every time — perfect for tests and demos." },
      ]}
      faqSubheading="Quick answers about the test data generator."
      faqs={faqs}
      softCtaText="Need a real database, seed scripts or staging environments built for your team?"
    >
      <ClientOnly fallback={<div className="min-h-[480px]" role="status" aria-live="polite"><span className="sr-only">Loading tool…</span></div>}><TestDataGenerator /></ClientOnly>
    </ToolPageShell>
  </>
);

export default TestDataGeneratorPage;